import moment from "moment";
import { DATE_FORMAT } from "../constants/dateConstants";

/**
 * Applies a resource's data filters to rows already loaded in the browser.
 *
 * Data filters are client-side: they trim what was fetched, they never re-query.
 * (Global filters are the other mechanism - those go into the request itself.)
 *
 * This was previously written inline inside the table widget, twice - once for the
 * dashboard view and once for the HTML report view - with three defects that are fixed
 * here:
 *
 *   1. Filters did not stack. Each one re-filtered the ORIGINAL rows and overwrote the
 *      result, so with two filters configured only the last one had any effect.
 *   2. "Contains" was never implemented. It fell through to the exact-match branch, so
 *      a Contains filter behaved as Equal and matched almost nothing.
 *   3. "Between" could never run. The loop skipped any filter without a `value`, but
 *      Between carries its bounds in `value1` / `value2` and leaves `value` empty.
 */

const hasValue = (v) => v !== null && v !== undefined && v !== "";

/**
 * Whether a filter is configured well enough to be worth applying. A filter with no
 * field, or with no value typed into it yet, is inert rather than matching nothing.
 */
export const isFilterActive = (filter) => {
  if (!filter || !filter.fieldName) return false;
  if (filter.filterType === "between") return hasValue(filter.value1) && hasValue(filter.value2);
  return hasValue(filter.value);
};

/** The display format declared for a column, if it has one. */
const formatFor = (availableFields, fieldName) =>
  (availableFields || []).find((f) => f?.fieldName === fieldName)?.format || DATE_FORMAT;

/* Both sides are put through the column's own format before comparing, so a column
   holding a full timestamp is compared at the precision the report actually shows -
   otherwise "equals 5 March" never matches a row stored as 5 March 09:41. */
const asDate = (value, format) => moment(moment(value).format(format));

const matchesDate = (rowValue, filter, format) => {
  const left = asDate(rowValue, format);
  if (!left.isValid()) return false;

  switch (filter.filterType) {
    case "gt":
      return left.isAfter(asDate(filter.value, format));
    case "gte":
      return left.isSameOrAfter(asDate(filter.value, format));
    case "lt":
      return left.isBefore(asDate(filter.value, format));
    case "lte":
      return left.isSameOrBefore(asDate(filter.value, format));
    case "between":
      /* Inclusive of both ends. The old code faked this by widening the bounds a day
         either side, which also pulled in the days outside the range. */
      return left.isBetween(asDate(filter.value1, format), asDate(filter.value2, format), undefined, "[]");
    default:
      return left.isSame(asDate(filter.value, format));
  }
};

const matchesNumber = (rowValue, filter) => {
  const left = Number(rowValue);
  if (Number.isNaN(left)) return false;

  switch (filter.filterType) {
    case "gt":
      return left > Number(filter.value);
    case "gte":
      return left >= Number(filter.value);
    case "lt":
      return left < Number(filter.value);
    case "lte":
      return left <= Number(filter.value);
    case "between":
      // Inclusive, matching the number inputs' "From / To" wording.
      return left >= Number(filter.value1) && left <= Number(filter.value2);
    default:
      return left === Number(filter.value);
  }
};

/* Both string operators ignore case, matching what the published board already does in
   witmeg-reporting-embed-v2 (TableWidget applyAllFilters). The same filter on the same
   board has to give the same rows in the editor's view and in the host app, and
   case-insensitive is the behaviour readers have today.

   Null and undefined cells are coerced rather than dereferenced - the package's
   `tc[fieldName].toLowerCase()` throws on an empty cell in a filtered column. */
const matchesString = (rowValue, filter) => {
  const left = String(rowValue ?? "").toLowerCase();
  const right = String(filter.value ?? "").toLowerCase();

  if (filter.filterType === "con") return left.includes(right);

  return left === right;
};

const matches = (row, filter, availableFields) => {
  const rowValue = row?.[filter.fieldName];

  if (filter.dataType === "date") return matchesDate(rowValue, filter, formatFor(availableFields, filter.fieldName));
  if (filter.dataType === "number") return matchesNumber(rowValue, filter);
  return matchesString(rowValue, filter);
};

/**
 * Narrows `rows` by every active filter in `dataFilters`.
 *
 * Filters stack: each one runs against what the previous one left, so applying a second
 * filter narrows the first result rather than replacing it.
 */
export function applyDataFilters(rows, dataFilters, availableFields) {
  if (!Array.isArray(rows)) return [];

  return (dataFilters || [])
    .filter(isFilterActive)
    .reduce((remaining, filter) => remaining.filter((row) => matches(row, filter, availableFields)), rows);
}

export default applyDataFilters;